import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import DriveBrowser from '../DriveBrowser';
import { DRIVE_ROOT_FOLDER_ID } from '@/types/docflow';
import type { INodeConfig } from '@/types/docflow';

interface DriveInputConfigProps {
  config: INodeConfig;
  onChange: (config: INodeConfig) => void;
}

const DriveInputConfig: React.FC<DriveInputConfigProps> = ({ config, onChange }) => {
  const { t } = useTranslation('docflow');
  const [showBrowser, setShowBrowser] = useState(false);

  return (
    <div className="space-y-4">
      <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300">
        {t('node.driveInput')}
      </h3>

      {/* Selected folder */}
      <div>
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
          {t('config.folder')}
        </label>
        <div className="flex items-center gap-2">
          <div className="flex-1 text-sm px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-gray-50 dark:bg-gray-700 dark:text-gray-200 truncate">
            {config.folder_name || 'No folder selected'}
          </div>
          <button
            type="button"
            onClick={() => setShowBrowser(true)}
            className="px-3 py-2 text-sm bg-blue-100 dark:bg-blue-900 text-blue-700 dark:text-blue-300 rounded-lg hover:bg-blue-200 dark:hover:bg-blue-800"
          >
            {t('config.browseFolders')}
          </button>
        </div>
      </div>

      {/* File filter */}
      <div>
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
          {t('config.fileFilter')}
        </label>
        <select
          value={config.file_filter || 'all'}
          onChange={(e) => onChange({ ...config, file_filter: e.target.value })}
          className="w-full text-sm px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 dark:text-gray-200"
        >
          <option value="all">{t('config.allFiles')}</option>
          <option value="pdf">{t('config.pdfOnly')}</option>
          <option value="image">{t('config.imageOnly')}</option>
        </select>
      </div>

      {showBrowser && (
        <DriveBrowser
          isOpen={showBrowser}
          onClose={() => setShowBrowser(false)}
          mode="folder"
          rootFolderId={DRIVE_ROOT_FOLDER_ID}
          onSelectFolder={(folderId, folderName) => {
            onChange({ ...config, folder_id: folderId, folder_name: folderName });
          }}
        />
      )}
    </div>
  );
};

export default DriveInputConfig;
